const DETECTED_TYPES = ["date", "datetime", "time", "number", "boolean", "string"];

export const IMPORT_FILE_ACCEPT = {
  "text/csv": [".csv"],
  "application/json": [".json"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

export function normalizePhone(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  if (digits.length > 10) return digits.slice(-10);
  return digits;
}

export function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(String(value).replace(/[$,%\s]/g, ""));
  return Number.isFinite(number) ? number : null;
}

export function toDateTime(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

export function minutesBetween(from, to) {
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

export function addMinutes(from, minutes) {
  const start = new Date(from);
  const amount = Number(minutes);
  if (Number.isNaN(start.getTime()) || !Number.isFinite(amount)) return null;
  return new Date(start.getTime() + (amount * 60000)).toISOString();
}

export function roundUpToIncrement(value, increment) {
  const amount = Number(value);
  const step = Number(increment);
  if (!Number.isFinite(amount)) return null;
  if (!Number.isFinite(step) || step <= 0) return amount;
  return Math.ceil(amount / step) * step;
}

export function coalesce(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== "");
}

function csvRows(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (quoted) {
      if (char === "\"" && next === "\"") {
        cell += "\"";
        i += 1;
      } else if (char === "\"") {
        quoted = false;
      } else {
        cell += char;
      }
      continue;
    }

    if (char === "\"") {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (char !== "\r") {
      cell += char;
    }
  }

  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }

  return rows;
}

function headerLooksPresent(row) {
  if (!row?.length) return false;
  const values = row.map((value) => String(value ?? "").trim()).filter(Boolean);
  if (!values.length) return false;
  const unique = new Set(values.map((value) => value.toLowerCase()));
  const stringish = values.filter((value) => toNumber(value) == null && !toDateTime(value)).length;
  return unique.size === values.length && stringish >= Math.ceil(values.length / 2);
}

function safeColumnKey(label, index) {
  const base = String(label || `Column ${index + 1}`)
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "") || `column_${index + 1}`;
  return `${base}_${index + 1}`;
}

function detectType(values) {
  const sample = values.filter((value) => value !== null && value !== undefined && value !== "").slice(0, 25);
  if (!sample.length) return "unknown";

  const counts = DETECTED_TYPES.reduce((memo, key) => ({ ...memo, [key]: 0 }), {});
  sample.forEach((value) => {
    const text = String(value).trim();
    if (/^(true|false|yes|no)$/i.test(text)) counts.boolean += 1;
    if (toNumber(value) != null) counts.number += 1;
    if (/^\d{1,2}:\d{2}(\s?[AP]M)?$/i.test(text)) counts.time += 1;
    if (toDateTime(value)) {
      counts.date += 1;
      counts.datetime += /[:T]/.test(text) ? 1 : 0;
    }
  });

  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  if (!best || best[1] < Math.ceil(sample.length * 0.6)) return "string";
  return best[0] === "date" && counts.datetime > 0 ? "datetime" : best[0];
}

function positiveIndex(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 ? Math.floor(number) : 1;
}

function dataStartFrom(options = {}) {
  const dataStart = options.dataStart || options;
  return {
    row: positiveIndex(dataStart.startRow ?? dataStart.row),
    column: positiveIndex(dataStart.startColumn ?? dataStart.column ?? dataStart.col),
  };
}

function hasCells(row) {
  return (row || []).some((value) => String(value ?? "").trim() !== "");
}

function tableFromStart(table, options) {
  const dataStart = dataStartFrom(options);
  const rows = (table || [])
    .map((row, index) => ({
      raw: (Array.isArray(row?.raw) ? row.raw : Array.isArray(row) ? row : [row]).slice(dataStart.column - 1),
      sourceRowIndex: positiveIndex(row?.sourceRowIndex ?? index + 1),
    }))
    .filter((row) => row.sourceRowIndex >= dataStart.row)
    .filter((row) => hasCells(row.raw));

  return { dataStart, rows };
}

function normalizeTableRows(table, options = {}) {
  const { dataStart, rows: tableRows } = tableFromStart(table, options);
  const hasHeader = headerLooksPresent(tableRows[0]?.raw);
  const headerRow = tableRows[0]?.raw || [];
  const headers = hasHeader ? headerRow : headerRow.map((_, index) => `Column ${index + 1}`);
  const dataRows = hasHeader ? tableRows.slice(1) : tableRows;
  const columns = headers.map((label, index) => ({
    key: safeColumnKey(label, index),
    label: String(label || `Column ${index + 1}`),
    index,
    sourceColumnIndex: dataStart.column + index,
  }));

  const rows = dataRows.map(({ raw, sourceRowIndex }) => {
    const values = {};
    columns.forEach((column, index) => {
      values[column.key] = raw[index] ?? "";
    });
    return {
      importRowId: `row-${sourceRowIndex}`,
      rowIndex: sourceRowIndex,
      raw,
      values,
    };
  });

  const typedColumns = columns.map((column) => ({
    ...column,
    detectedType: detectType(rows.map((row) => row.values[column.key])),
  }));

  return { columns: typedColumns, rows, sampleRows: rows.slice(0, 5), hasHeader, dataStart };
}

function normalizeObjectRows(items, options = {}) {
  const dataStart = dataStartFrom(options);
  const sourceItems = (items || []).slice(dataStart.row - 1);
  const labels = Array.from(new Set(items.flatMap((item) => Object.keys(item || {}))));
  const columns = labels.map((label, index) => ({
    key: safeColumnKey(label, index),
    label,
    sourceKey: label,
    index,
  }));

  const rows = sourceItems.map((raw, index) => {
    const rowIndex = dataStart.row + index;
    const values = {};
    columns.forEach((column) => {
      values[column.key] = raw?.[column.sourceKey] ?? "";
    });
    return {
      importRowId: `row-${rowIndex}`,
      rowIndex,
      raw,
      values,
    };
  });

  return {
    columns: columns.map((column) => ({
      ...column,
      detectedType: detectType(rows.map((row) => row.values[column.key])),
    })),
    rows,
    sampleRows: rows.slice(0, 5),
    hasHeader: true,
    dataStart,
  };
}

export async function parseImportFile(file, options = {}) {
  const name = String(file?.name || "").toLowerCase();
  const text = await file.text();

  if (file?.type === "application/json" || name.endsWith(".json")) {
    const parsed = JSON.parse(text);
    const items = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.rows) ? parsed.rows : [];
    if (!Array.isArray(items)) throw new Error("JSON import must contain an array of rows.");
    if (items.some((item) => Array.isArray(item))) return normalizeTableRows(items, options);
    return normalizeObjectRows(items, options);
  }

  if (file?.type === "text/csv" || name.endsWith(".csv")) {
    return normalizeTableRows(csvRows(text), options);
  }

  throw new Error("Only CSV and JSON files can be analyzed locally.");
}

function tokenize(expression = "") {
  const tokens = [];
  let i = 0;
  while (i < expression.length) {
    const char = expression[i];
    if (/\s/.test(char)) {
      i += 1;
    } else if ("()+-*/,".includes(char)) {
      tokens.push({ type: char, value: char });
      i += 1;
    } else if (char === "\"" || char === "'") {
      const quote = char;
      let value = "";
      i += 1;
      while (i < expression.length && expression[i] !== quote) {
        if (expression[i] === "\\" && i + 1 < expression.length) i += 1;
        value += expression[i];
        i += 1;
      }
      if (expression[i] !== quote) throw new Error("Unterminated string literal.");
      tokens.push({ type: "literal", value });
      i += 1;
    } else if (/\d|\./.test(char)) {
      let value = "";
      while (i < expression.length && /[\d.]/.test(expression[i])) {
        value += expression[i];
        i += 1;
      }
      const number = Number(value);
      if (!Number.isFinite(number)) throw new Error(`Invalid number: ${value}`);
      tokens.push({ type: "literal", value: number });
    } else if (/[A-Za-z_]/.test(char)) {
      let value = "";
      while (i < expression.length && /[A-Za-z0-9_.?]/.test(expression[i])) {
        value += expression[i];
        i += 1;
      }
      tokens.push({ type: "identifier", value: value.replace(/\?\./g, ".") });
    } else {
      throw new Error(`Unsupported character: ${char}`);
    }
  }
  return tokens;
}

function readPath(context, path) {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => (value == null ? null : value[key]), context);
}

export function evaluateImportFormula(expression, context = {}) {
  const tokens = tokenize(expression);
  let position = 0;
  const helpers = { roundUpToIncrement, coalesce, minutesBetween, toNumber, toDateTime, normalizePhone };
  const operators = {
    "+": { precedence: 1, fn: (a, b) => Number(a) + Number(b) },
    "-": { precedence: 1, fn: (a, b) => Number(a) - Number(b) },
    "*": { precedence: 2, fn: (a, b) => Number(a) * Number(b) },
    "/": { precedence: 2, fn: (a, b) => Number(a) / Number(b) },
  };
  const peek = () => tokens[position];
  const take = () => tokens[position++];

  const parsePrimary = () => {
    const token = take();
    if (!token) throw new Error("Unexpected end of formula.");
    if (token.type === "literal") return token.value;
    if (token.type === "-") return Number(parsePrimary()) * -1;
    if (token.type === "(") {
      const value = parseBinary(0);
      if (take()?.type !== ")") throw new Error("Missing closing parenthesis.");
      return value;
    }
    if (token.type === "identifier") {
      if (peek()?.type === "(") {
        take();
        const args = [];
        if (peek()?.type !== ")") {
          while (true) {
            args.push(parseBinary(0));
            if (peek()?.type !== ",") break;
            take();
          }
        }
        if (take()?.type !== ")") throw new Error("Missing function closing parenthesis.");
        const fn = helpers[token.value];
        if (!fn) throw new Error(`Formula helper is not allowed: ${token.value}`);
        return fn(...args);
      }
      return readPath(context, token.value);
    }
    throw new Error(`Unexpected token: ${token.value}`);
  };

  const parseBinary = (minimum) => {
    let left = parsePrimary();
    while (operators[peek()?.type] && operators[peek().type].precedence >= minimum) {
      const operator = take().type;
      const config = operators[operator];
      const right = parseBinary(config.precedence + 1);
      left = config.fn(left, right);
    }
    return left;
  };

  const value = parseBinary(0);
  if (position < tokens.length) throw new Error(`Unexpected formula token: ${tokens[position].value}`);
  return value;
}
